import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { OrderItem } from "../models/IOrders";
import { getOrderHistory } from "../services/orderHistory";
import { getUserToken } from "./tokenUtils";

const toApiDate = (d: Date) => {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const y = d.getFullYear();
  return `${m}.${day}.${y}`;
};

interface IActiveOrderContext {
  activeOrders: OrderItem[];
  refreshActiveOrder: () => void;
  updateOrderStatus: (statusNameTr: string, statusNameEn: string) => void;
  deliveredOrder: OrderItem | null;
  clearDeliveredOrder: () => void;
}

const ActiveOrderCtx = createContext<IActiveOrderContext>({
  activeOrders: [],
  refreshActiveOrder: () => {},
  updateOrderStatus: () => {},
  deliveredOrder: null,
  clearDeliveredOrder: () => {},
});

export const useActiveOrder = () => useContext(ActiveOrderCtx);

export const ActiveOrderProvider: FC<PropsWithChildren> = ({ children }) => {
  const [activeOrders, setActiveOrders] = useState<OrderItem[]>([]);
  const [deliveredOrder, setDeliveredOrder] = useState<OrderItem | null>(null);
  const activeOrdersRef = useRef<OrderItem[]>([]);

  useEffect(() => {
    activeOrdersRef.current = activeOrders;
  }, [activeOrders]);

  const fetchActiveOrder = useCallback(() => {
    const token = getUserToken();
    if (!token) {
      setActiveOrders([]);
      return;
    }
    const yesterday = toApiDate(new Date(Date.now() - 864e5));
    const tomorrow = toApiDate(new Date(Date.now() + 864e5));
    getOrderHistory(yesterday, tomorrow)
      .then((res) => {
        const actives = res.data.filter((o) => o.isOrderEnabled);
        setActiveOrders(actives);
      })
      .catch(() => {});
  }, []);

  const updateOrderStatus = useCallback((statusNameTr: string, statusNameEn: string) => {
    const prevList = activeOrdersRef.current;
    if (prevList.length === 0) return;
    const isDelivered =
      statusNameTr.toLowerCase().includes("teslim edildi") ||
      statusNameEn.toLowerCase().includes("delivered");
    if (isDelivered) {
      const last = prevList[prevList.length - 1];
      const updated = {
        ...last,
        orderStatus: { ...last.orderStatus, orderStatusNameTr: statusNameTr, orderStatusNameEn: statusNameEn },
      };
      setDeliveredOrder(updated);
      setActiveOrders((prev) => prev.filter((o) => o.id !== last.id));
    } else {
      setActiveOrders((prev) =>
        prev.map((o, i) =>
          i === prev.length - 1
            ? { ...o, orderStatus: { ...o.orderStatus, orderStatusNameTr: statusNameTr, orderStatusNameEn: statusNameEn } }
            : o
        )
      );
    }
  }, []);

  useEffect(() => {
    fetchActiveOrder();
  }, [fetchActiveOrder]);

  const clearDeliveredOrder = useCallback(() => setDeliveredOrder(null), []);

  return (
    <ActiveOrderCtx.Provider value={{ activeOrders, refreshActiveOrder: fetchActiveOrder, updateOrderStatus, deliveredOrder, clearDeliveredOrder }}>
      {children}
    </ActiveOrderCtx.Provider>
  );
};
